import { Clipboard } from '@angular/cdk/clipboard';
import { DatePipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { ConfirmationService, MessageService, PrimeIcons } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { lastValueFrom, map } from 'rxjs';
import {
  FinancialAdminControllerService,
  ItauCobrancaRecorrenteResponse,
  ItauRecorrenciaResponse
} from 'src/app/admin-api';
import { TableActionButton, TableColumn } from 'src/app/components/table';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { FormUtil } from 'src/app/utils/form.util';
import { CobrancasRecorrentesModalComponent } from './cobrancas-recorrentes-modal/cobrancas-recorrentes-modal.component';

@Component({
  selector: 'app-recorrencias',
  templateUrl: './recorrencias.component.html',
  styleUrl: './recorrencias.component.scss',
  providers: [DialogService, DatePipe]
})
export class RecorrenciasComponent implements OnInit {
  private financialAdminService = inject(FinancialAdminControllerService);
  private messageService = inject(MessageService);
  private clipboard = inject(Clipboard);
  private confirmationService = inject(ConfirmationService);
  private dialogService = inject(DialogService);
  private datePipe = inject(DatePipe);

  motivoEncerramento = (recurrence: ItauRecorrenciaResponse) => {
    if (
      !recurrence?.encerramento?.cancelamento &&
      !recurrence?.encerramento?.rejeicao?.descricao
    )
      return null;
    return (
      recurrence.encerramento?.rejeicao?.descricao ||
      `${recurrence.encerramento.cancelamento.descricao} - ${recurrence.encerramento.cancelamento.solicitante}`
    );
  };

  askCancelRecurrence = (
    recurrence: ItauRecorrenciaResponse,
    $event: MouseEvent
  ) => {
    this.confirmationService.confirm({
      acceptButtonStyleClass: 'p-button-danger',
      acceptLabel: 'Cancelar',
      rejectLabel: 'Manter',
      rejectButtonStyleClass: 'p-button-primary',
      message: `Tem certeza que deseja cancelar a recorrência "${recurrence.idRec}"?`,
      header: 'Confirmação',
      icon: PrimeIcons.EXCLAMATION_TRIANGLE,
      target: $event.target,
      accept: async () => await this.cancelRecurrence(recurrence)
    });
  };
  findAndShowCobrancasRecorrentes = async (
    recurrence: ItauRecorrenciaResponse
  ) => {
    if (!recurrence.cobsr) {
      try {
        LoaderService.showLoader();
        recurrence.cobsr = await lastValueFrom(
          this.financialAdminService
            .findCobrancasRecorrentes(
              (
                recurrence.atualizacao?.at(0)?.data as any as Date
              )?.toISOString(),
              new Date().toISOString(),
              recurrence.idRec
            )
            .pipe(map((data) => data.result))
        );
      } catch (error) {
        AppDialogService.showErrorDialog(error);
        recurrence.cobsr = [];
      } finally {
        LoaderService.showLoader(false);
      }
    }
    this.dialogService.open(CobrancasRecorrentesModalComponent, {
      header: `Cobranças da recorrência ${recurrence.idRec}`,
      width: '80%',
      modal: true,
      maximizable: true,
      data: {
        recurrence
      }
    });
  };
  cols = [
    new TableColumn.Builder().setHeader('ID Rec').setField('idRec').build(),
    new TableColumn.Builder()
      .setHeader('Data criação')
      .setField('atualizacao.0.data')
      .setRequired(true)
      .setType('date')
      .setCondition('between')
      .build(),
    new TableColumn.Builder()
      .setHeader('TxId Inicial')
      .setField('ativacao.dadosJornada.txid')
      .build(),
    new TableColumn.Builder()
      .setHeader('Nome')
      .setField('vinculo.devedor.nome')
      .build(),
    new TableColumn.Builder()
      .setHeader('CPF')
      .setField('vinculo.devedor.cpf')
      .setType('number')
      .setDisplayFunction((recurrence: ItauRecorrenciaResponse) =>
        recurrence.vinculo?.devedor.cpf
          ? FormUtil.cpfFormatted(
              FormUtil.onlyNumbers(recurrence.vinculo?.devedor.cpf)
            )
          : null
      )
      .build(),
    new TableColumn.Builder()
      .setHeader('Contrato (CreditCardPaymentId)')
      .setField('vinculo.contrato')
      .build(),
    new TableColumn.Builder()
      .setHeader('Contrato')
      .setField('vinculo.contrato')
      .build(),
    new TableColumn.Builder()
      .setHeader('Data início')
      .setField('calendario.dataInicial')
      .setType('date')
      .setCondition('between')
      .setDisplayFunction((recurrence: ItauRecorrenciaResponse) =>
        this.datePipe.transform(
          recurrence.calendario?.dataInicial,
          'dd/MM/yyyy',
          'UTC'
        )
      )
      .build(),
    new TableColumn.Builder()
      .setHeader('Periodicidade')
      .setField('calendario.periodicidade')
      .build(),
    new TableColumn.Builder().setHeader('Status').setField('status').build(),
    new TableColumn.Builder()
      .setHeader('Jornada')
      .setField('dadosQR.jornada')
      .setDisplayFunction(
        (recurrence) =>
          recurrence.dadosQR?.jornada || recurrence.ativacao?.tipoJornada
      )
      .build(),
    new TableColumn.Builder()
      .setHeader('Motivo encerramento')
      .setField('encerramento')
      .setDisplayFunction(this.motivoEncerramento)
      .build(),
    new TableColumn.Builder()
      .setHeader('Ação')
      .setField('dadosQR.pixCopiaECola')
      .setType('button')
      .build()
  ];
  colsCob = [
    new TableColumn.Builder()
      .setHeader('Criação')
      .setField('calendario.criacao')
      .setType('date')
      .setCondition('between')
      .setRequired(true)
      .build(),
    new TableColumn.Builder().setHeader('TxId').setField('txid').build(),
    new TableColumn.Builder().setHeader('IdRec').setField('idRec').build(),
    new TableColumn.Builder()
      .setHeader('Email')
      .setField('devedor.email')
      .build(),
    new TableColumn.Builder()
      .setHeader('Vencimento')
      .setField('calendario.dataDeVencimento')
      .setType('date')
      .setCondition('between')
      .build(),
    new TableColumn.Builder().setHeader('Status').setField('status').build(),
    new TableColumn.Builder()
      .setHeader('Valor')
      .setField('valor.original')
      .setType('currency')
      .build(),
    new TableColumn.Builder()
      .setHeader('Retentativa')
      .setField('politicaRetentativa')
      .build(),
    new TableColumn.Builder()
      .setHeader('Ação')
      .setField('')
      .setType('button')
      .build()
  ];

  buttons = [
    new TableActionButton.Builder()
      .setIcon(PrimeIcons.COPY)
      .setTooltip('Copiar código pix')
      .setTooltipPosition('bottom')
      .setAction(async (recurrence: ItauRecorrenciaResponse) => {
        if (
          !recurrence?.dadosQR?.pixCopiaECola &&
          recurrence?.ativacao?.dadosJornada?.txid
        )
          try {
            LoaderService.showLoader();
            recurrence = await lastValueFrom(
              this.financialAdminService
                .getRecurrenceDetails(
                  recurrence.idRec,
                  recurrence.ativacao.dadosJornada.txid
                )
                .pipe(map((data) => data.result as ItauRecorrenciaResponse))
            );
          } catch (error) {
            AppDialogService.showErrorDialog(error);
          } finally {
            LoaderService.showLoader(false);
          }
        if (recurrence?.dadosQR?.pixCopiaECola) {
          this.recurrences
            ?.filter((r) => r.idRec === recurrence.idRec)
            .forEach((r) => (r.dadosQR = recurrence.dadosQR));
          this.copyToClipboard(recurrence?.dadosQR?.pixCopiaECola);
        }
      })
      .setDisplay(
        (r: ItauRecorrenciaResponse) =>
          r?.ativacao?.dadosJornada?.txid?.trim()?.length > 0 &&
          r.status === ItauRecorrenciaResponse.StatusEnum.CRIADA
      )
      .setText(true)
      .build(),
    new TableActionButton.Builder()
      .setIcon(PrimeIcons.TIMES_CIRCLE)
      .setTooltip('Cancelar recorrência')
      .setTooltipPosition('bottom')
      .setSeverity('danger')
      .setAction(this.askCancelRecurrence)
      .setDisplay((r: ItauRecorrenciaResponse) =>
        [
          ItauRecorrenciaResponse.StatusEnum.CRIADA,
          ItauRecorrenciaResponse.StatusEnum.APROVADA
        ].includes(r.status)
      )
      .setText(true)
      .build(),
    new TableActionButton.Builder()
      .setIcon(PrimeIcons.DOLLAR)
      .setTooltip('Ver cobranças')
      .setTooltipPosition('bottom')
      .setSeverity('primary')
      .setAction(this.findAndShowCobrancasRecorrentes)
      .setText(true)
      .setDisplay(
        (r: ItauRecorrenciaResponse) =>
          ![
            ItauRecorrenciaResponse.StatusEnum.CRIADA,
            ItauRecorrenciaResponse.StatusEnum.REJEITADA
          ].includes(r.status)
      )
      .build()
  ];
  dropdownFilters = {
    status: Object.keys(ItauRecorrenciaResponse.StatusEnum)
      .map((key) => ({
        label: key,
        value: ItauRecorrenciaResponse.StatusEnum[key]
      }))
      .concat([{ label: 'Todos', value: null }])
  };
  defaultFilters = {
    'atualizacao.0.data': [...this.startRange],
    status: null
  };
  recurrences: ItauRecorrenciaResponse[];
  dropdownFiltersCob = {
    status: Object.keys(ItauCobrancaRecorrenteResponse.StatusEnum)
      .map((key) => ({
        label: key,
        value: ItauCobrancaRecorrenteResponse.StatusEnum[key]
      }))
      .concat([{ label: 'Todos', value: null }]),
    politicaRetentativa: Object.keys(
      ItauCobrancaRecorrenteResponse.PoliticaRetentativaEnum
    )
      .map((key) => ({
        label: ItauCobrancaRecorrenteResponse.PoliticaRetentativaEnum[key],
        value: ItauCobrancaRecorrenteResponse.PoliticaRetentativaEnum[key]
      }))
      .concat([{ label: 'Todos', value: null }])
  };

  async ngOnInit() {
    await this.findRecurrences();
  }

  async findRecurrences(range?: Date[]) {
    try {
      LoaderService.showLoader();
      this.recurrences = await lastValueFrom(
        this.financialAdminService
          .findRecurrences(
            (range || this.startRange)?.at(0)?.toISOString(),
            (range || this.startRange)?.at(1)?.toISOString()
          )
          .pipe(
            map((data) =>
              data.result.map((r: ItauRecorrenciaResponse) => {
                if (r.atualizacao?.at(0)?.data)
                  r.atualizacao[0].data = new Date(
                    r.atualizacao[0].data
                  ) as any;
                if (r.calendario?.dataInicial)
                  r.calendario.dataInicial = new Date(
                    r.calendario.dataInicial
                  ) as any;
                return r;
              })
            )
          )
      );
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  copyToClipboard = (value: string) => {
    this.clipboard.copy(value);
    this.messageService.add({
      severity: 'info',
      summary: 'Copiado',
      detail: 'Código copiado para área de transferência'
    });
  };

  async cancelRecurrence(recurrence: ItauRecorrenciaResponse) {
    if (recurrence.status !== ItauRecorrenciaResponse.StatusEnum.CANCELADA) {
      try {
        LoaderService.showLoader();
        recurrence = await lastValueFrom(
          this.financialAdminService
            .cancelRecurrence(recurrence.idRec)
            .pipe(map((data) => data.result))
        );
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Recorrência cancelada com sucesso'
        });
        this.recurrences
          ?.filter((r) => r.idRec === recurrence.idRec)
          .forEach((r) => {
            r.status = recurrence.status;
            r.encerramento = {
              rejeicao: null,
              cancelamento: {
                solicitante: 'PSP_RECEBEDOR',
                codigo: 'SLCR',
                descricao: 'Cancelamento solicitado pelo usuário recebedor'
              }
            };
          });
      } catch (error) {
        AppDialogService.showErrorDialog(error);
      } finally {
        LoaderService.showLoader(false);
      }
    }
  }

  get startRange() {
    const date = new Date(new Date().setDate(new Date().getDate() - 1));
    date.setHours(0, 0, 0, 0);
    return [date, new Date()];
  }
}
